import './EditSensorModal.css';

import React, { useEffect, useState } from 'react';
import { useLoginContext } from '../../../App';
import { useContentContext } from '../../layout/Layout';
import B2BPopUpForm from '../../b2b-common/B2BPopUpForm';
import { useIotFreworksContext } from '../pages/SensorsExplorerPage';
import EditText from '../../b2b-common/EditText';
import Banner from '../../b2b-common/Banner';
import ConfigurationJSONCreator from '../components/ConfigurationJSONCreator';
import { fullImagePath, generateSelectField } from '../../b2b-common/helpers';
import { saveNewSensor, updateSensor } from '../../../services/b2b-iotsampling-apiService';
import { showNotification } from '../../b2b-common/Notification';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';

const EditSensorModal = ({ sensor = null, open, onSave, onCancel }) => {
    const { setNotification } = useContentContext();
    const { loginData } = useLoginContext();
    const { currentFramework, currentFwMasterConfigs } = useIotFreworksContext();
    const initialJsonConfig = sensor && sensor.sourceConfiguration ? JSON.parse(sensor.sourceConfiguration) || {} : {};
    const [jsonConfig, setJsonConfig] = useState(initialJsonConfig);
    const { t } = useB2BTranslation('b2b-sampling');

    const level1Options = currentFramework && currentFramework.subLevels
        ? currentFramework.subLevels.map(subLevel => ({
            value: subLevel.levelN_ItemId,
            label: `${subLevel.levelN_ItemStrId} - ${subLevel.levelN_ItemName}`,
        }))
        : [];
    const level1 = currentFramework.levelDefinitions.find(fw => fw.fwLevelId === 1);

    const newSensor = {
        sourceConfigId: null,
        organizationUUID: loginData.loggedOrganizationUuid,
        sourceName: t('New sensor name'),
        sourceDescription: t('Sensor description'),
        frameworkId: currentFramework.frameworkId,
        levelN_ItemId: currentFramework.subLevels[0].levelN_ItemId,
        sourceConfiguration: currentFwMasterConfigs && currentFwMasterConfigs.length > 0 ?
            currentFwMasterConfigs.find(c => (c.frameworkId === currentFramework.frameworkId &&
                c.levelN_ItemId === currentFramework.subLevels[0].levelN_ItemId))?.readableSensorConfigurationDTO ?? '{}'
            :
            '{}',
    };

    const [currentSensor, setCurrentSensor] = useState(sensor ? sensor : newSensor);

    useEffect(() => {
        if (sensor !== null) {
            setCurrentSensor(sensor);
            const jsonConfig = sensor && sensor.sourceConfiguration ? JSON.parse(sensor.sourceConfiguration) || {} : {};
            setJsonConfig(jsonConfig);
        } else {
            setCurrentSensor(newSensor);
            const jsonConfig = currentFwMasterConfigs && currentFwMasterConfigs.length > 0 ?
                currentFwMasterConfigs.find(c => (c.frameworkId === currentFramework.frameworkId &&
                    c.levelN_ItemId === currentFramework.subLevels[0].levelN_ItemId))?.readableSensorConfigurationDTO ?? '{}'
                :
                '{}';
            setJsonConfig(jsonConfig);
        }
    }, [sensor]);

    const handleChangeOfModel = (event) => {
        console.log('handleChangeOfModel:', event.target.value);
        const levelN_ItemId = event.target.value;
        setCurrentSensor({ ...currentSensor, levelN_ItemId });
        const jsonConfig = currentFwMasterConfigs && currentFwMasterConfigs.length > 0 ?
            currentFwMasterConfigs.find(c => (c.frameworkId === currentFramework.frameworkId &&
                c.levelN_ItemId === levelN_ItemId))?.readableSensorConfigurationDTO ?? '{}'
            :
            '{}';
        setJsonConfig(jsonConfig);
    }

    const handleSave = async () => {
        if (sensor === null) { // new sensor
            const sensorDTO = {
                ...currentSensor,
                sourceConfiguration: JSON.stringify(jsonConfig),
            };
            try {
                const newSensor = await saveNewSensor(loginData.accessToken, loginData.loggedOrganizationUuid, sensorDTO);
                showNotification(setNotification, t('New sensor saved successfully'), 'success', 3000);
                onSave && onSave(newSensor);
            } catch (error) {
                showNotification(setNotification, t('Error saving new sensor: ') + error.message, 'error', 5000);
            }
        } else { // edit sensor
            const sensorDTO = {
                ...currentSensor,
                sourceConfiguration: JSON.stringify(jsonConfig),
            };
            try {
                const updatedSensor = await updateSensor(loginData.accessToken, loginData.loggedOrganizationUuid, sensorDTO);
                showNotification(setNotification, t('Sensor updated successfully'), 'success', 3000);
                onSave && onSave(updatedSensor);
            } catch (error) {
                showNotification(setNotification, t('Error updating sensor: ') + error.message, 'error', 5000);
            }
        }
    }

    const handleCancel = () => {
        onCancel && onCancel();
    }

    return (
        <B2BPopUpForm
            open={open}
            title={!sensor ? t('New Sensor') : t('Edit Sensor: ') + sensor.sourceName}
            onClose={handleCancel}
            className={'sensor-edit-modal'}
            buttons={[
                { label: t('Save'), onClick: handleSave },
                { label: t('Cancel'), onClick: handleCancel },
            ]}
        >
            <div className='sensor-edit-modal-header'>
                <div className='sensor-edit-modal-model-image'>
                    <img src={fullImagePath(currentFramework.subLevels.find(lvl => lvl.levelN_ItemId === currentSensor.levelN_ItemId).imagePath)} alt={currentFramework.subLevels.find(lvl => lvl.levelN_ItemId === currentSensor.levelN_ItemId).levelN_ItemName} />
                </div>
                <div className='sensor-edit-modal-basic-info'>
                    {generateSelectField(level1Options,
                        {
                            label: level1 ? level1.fwLevelName : 'Level 1',
                            value: currentSensor.levelN_ItemId,
                            onChange: handleChangeOfModel,
                            className: 'sensor-edit-modal-basic-info-model',
                        })}
                    <EditText object={currentSensor} objectField='sourceName' setObjectField={setCurrentSensor} label={t('Sensor name')} />
                    <EditText object={currentSensor} objectField='sourceDescription' setObjectField={setCurrentSensor} label={t('Sensor description')} multiline={true} rows={3} />
                </div>
            </div>
            <div className='sensor-edit-modal-configuration'>
                <Banner message={t('Sensor specific configuration')} underline={false} headingLevel='h3' />
                <ConfigurationJSONCreator jsonConfig={jsonConfig} setJsonConfig={setJsonConfig} isMasterConfiguration={false} />
            </div>
        </B2BPopUpForm>
    );
}

export default EditSensorModal;